import { useCallback, useEffect, useRef, useState } from 'react';

// How often to re-poll `version.json` while the tab is visible. Deliberately
// slow — the focus/visibility triggers below catch the common "reopened an old
// tab after a deploy" case; the interval is just a backstop for a tab left
// open and focused for a long time.
const CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

// Debounce window so focus-storms (alt-tabbing, DevTools, etc.) can't spam the
// network with version.json fetches.
const CHECK_DEBOUNCE_MS = 10 * 1000; // 10 seconds

export interface VersionState {
  /** True once a newer build has been detected (and stays true — we stop polling). */
  outdated: boolean;
  /** The buildId reported by version.json when it differs from __BUILD_ID__. */
  latestBuildId: string | null;
}

/**
 * Detects when the running SPA is stale relative to the deployed build.
 *
 * Fetches `/version.json` (published `no-cache` by the deploy scripts) and
 * compares its `buildId` against the bundle-baked `__BUILD_ID__`. A mismatch
 * means CloudFront is now serving a newer build than the one this tab loaded
 * — the classic "open tab references a lazy chunk hash the /*-invalidation
 * deleted, so the route 404s" failure (the CEO's stale-signup incident).
 *
 * Checks on: mount, window `focus`, `visibilitychange`→visible, and a slow
 * interval while visible. Fully fail-silent — a network error NEVER blocks or
 * disrupts the app. Once outdated, polling stops (the arming is one-way; the
 * caller decides when to actually reload at a safe moment).
 *
 * @param enabled Pass `false` to fully disable the checker (e.g. inside the
 *   embedded widget iframe route, which must never self-reload).
 */
export function useVersionCheck(enabled: boolean = true): VersionState {
  const [state, setState] = useState<VersionState>({
    outdated: false,
    latestBuildId: null,
  });

  const lastCheckRef = useRef(0);
  const inFlightRef = useRef(false);
  const outdatedRef = useRef(false);

  const check = useCallback(async () => {
    if (!enabled) return;
    if (outdatedRef.current) return; // already stale — nothing more to learn
    if (inFlightRef.current) return;

    const now = Date.now();
    if (now - lastCheckRef.current < CHECK_DEBOUNCE_MS) return;
    lastCheckRef.current = now;
    inFlightRef.current = true;

    try {
      const res = await fetch(`/version.json?ts=${now}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const latest = typeof data?.buildId === 'string' ? data.buildId : null;
      if (latest && latest !== __BUILD_ID__) {
        outdatedRef.current = true;
        setState({ outdated: true, latestBuildId: latest });
      }
    } catch {
      // Fail-silent: offline, blocked, or version.json missing (older deploy)
      // must never surface an error or block the app.
    } finally {
      inFlightRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    check(); // initial check on mount

    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, [enabled, check]);

  return state;
}
